import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPE_LABELS = { probefahrt: "Probefahrt", kontakt: "Kontakt", flotte: "Flotte" };
const STATUS_LABELS = { neu: "Neu", in_bearbeitung: "In Bearbeitung", erledigt: "Erledigt" };

export default function Admin() {
  const [user, setUser] = useState(undefined); // undefined=checking, null=logged out
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [enquiries, setEnquiries] = useState([]);
  const [models, setModels] = useState([]);
  const [edits, setEdits] = useState({});

  const loadData = async () => {
    try {
      const [e, m] = await Promise.all([api.get("/admin/enquiries"), api.get("/models")]);
      setEnquiries(e.data);
      setModels(m.data);
      setEdits(Object.fromEntries(m.data.map((x) => [x.slug, {
        price_text: x.price_text, status: x.status,
      }])));
    } catch { /* handled by caller context */ }
  };

  const checkSession = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      try {
        await api.post("/auth/refresh");
        const r = await api.get("/auth/me");
        setUser(r.data);
      } catch {
        setUser(null);
      }
    }
  };

  useEffect(() => { checkSession(); }, []);
  useEffect(() => { if (user) loadData(); }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const r = await api.post("/auth/login", { email, password });
      setUser(r.data);
      toast.success("Angemeldet");
    } catch (err) {
      setLoginError(formatApiError(err));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      setEnquiries((list) => list.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Status aktualisiert");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveModel = async (slug) => {
    try {
      const r = await api.put(`/admin/models/${slug}`, edits[slug]);
      setModels((list) => list.map((x) => (x.slug === slug ? r.data : x)));
      toast.success(`${r.data.name} gespeichert`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (user === undefined) {
    return <div className="max-w-md mx-auto px-6 py-32 text-slate-500">Sitzung wird geprüft …</div>;
  }

  if (!user) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6 py-24 bg-[#F4F7F9]" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm border border-slate-200 bg-white p-8">
          <h1 className="text-2xl font-bold tracking-tight text-[#0A2540]">Admin-Login</h1>
          <p className="mt-2 text-sm text-slate-600">SkyworthCars CMS – Modelle &amp; Anfragen verwalten.</p>
          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="admin-email">E-Mail</Label>
              <Input id="admin-email" data-testid="admin-email-input" type="email" required
                     value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm mt-1.5" />
            </div>
            <div>
              <Label htmlFor="admin-password">Passwort</Label>
              <Input id="admin-password" data-testid="admin-password-input" type="password" required
                     value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm mt-1.5" />
            </div>
            {loginError && (
              <p className="text-sm text-red-600" data-testid="admin-login-error">{loginError}</p>
            )}
            <Button type="submit" data-testid="admin-login-submit"
                    className="w-full rounded-md bg-[#0A2540] hover:bg-slate-800 text-white py-3">
              Anmelden
            </Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="admin-dashboard">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#0A2540]">SkyworthCars CMS</h1>
          <p className="text-sm text-slate-600 mt-1">Angemeldet als {user.email}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={loadData} data-testid="admin-refresh-btn"
                  className="rounded-md border-slate-300">
            <RefreshCw className="w-4 h-4 mr-2" /> Aktualisieren
          </Button>
          <Button variant="outline" onClick={logout} data-testid="admin-logout-btn"
                  className="rounded-md border-slate-300">
            <LogOut className="w-4 h-4 mr-2" /> Abmelden
          </Button>
        </div>
      </div>

      <Tabs defaultValue="enquiries" className="mt-10">
        <TabsList className="bg-[#F4F7F9] border border-slate-200 rounded-md">
          <TabsTrigger value="enquiries" data-testid="tab-enquiries">
            Anfragen ({enquiries.length})
          </TabsTrigger>
          <TabsTrigger value="models" data-testid="tab-models">Modelle &amp; Preise</TabsTrigger>
        </TabsList>

        <TabsContent value="enquiries" className="mt-6">
          <div className="border border-slate-200 overflow-x-auto" data-testid="enquiries-table">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200">
                  <TableHead>Datum</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Kontakt</TableHead>
                  <TableHead>Modell / Firma</TableHead>
                  <TableHead>Nachricht</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enquiries.length === 0 && (
                  <TableRow className="border-slate-200">
                    <TableCell colSpan={7} className="text-center text-slate-500 py-10">
                      Noch keine Anfragen eingegangen.
                    </TableCell>
                  </TableRow>
                )}
                {enquiries.map((e) => (
                  <TableRow key={e.id} className="border-slate-200" data-testid={`enquiry-row-${e.id}`}>
                    <TableCell className="whitespace-nowrap text-sm text-slate-600">
                      {new Date(e.created_at).toLocaleString("de-CH")}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="rounded-sm">{TYPE_LABELS[e.type] || e.type}</Badge>
                    </TableCell>
                    <TableCell className="font-semibold text-[#0A2540]">{e.name}</TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {e.email}{e.phone ? <><br />{e.phone}</> : null}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {e.model || "-"}{e.company ? <><br />{e.company}</> : null}
                      {e.preferred_date ? <><br />Wunsch: {e.preferred_date}</> : null}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-xs">{e.message || "-"}</TableCell>
                    <TableCell>
                      <Select value={e.status} onValueChange={(v) => setStatus(e.id, v)}>
                        <SelectTrigger className="rounded-sm bg-white w-40" data-testid={`enquiry-status-${e.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          {Object.entries(STATUS_LABELS).map(([v, l]) => (
                            <SelectItem key={v} value={v}>{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="models" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="admin-models-grid">
            {models.map((m) => (
              <div key={m.slug} className="border border-slate-200 bg-white p-6" data-testid={`admin-model-${m.slug}`}>
                <div className="flex items-center gap-4">
                  <img src={m.image} alt={m.name} className="w-24 h-16 object-cover border border-slate-200" />
                  <div>
                    <h3 className="font-bold text-[#0A2540]">{m.name}</h3>
                    <p className="text-xs text-slate-500">{m.category}</p>
                  </div>
                </div>
                <div className="mt-5 space-y-4">
                  <div>
                    <Label>Preis (Anzeigetext, z. B. «ab CHF 39'900.–»)</Label>
                    <Input value={edits[m.slug]?.price_text ?? ""} data-testid={`admin-price-${m.slug}`}
                           onChange={(e) => setEdits((s) => ({ ...s, [m.slug]: { ...s[m.slug], price_text: e.target.value } }))}
                           className="rounded-sm mt-1.5" />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={edits[m.slug]?.status ?? m.status}
                            onValueChange={(v) => setEdits((s) => ({ ...s, [m.slug]: { ...s[m.slug], status: v } }))}>
                      <SelectTrigger className="rounded-sm mt-1.5 bg-white" data-testid={`admin-status-${m.slug}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {["Verfügbar", "Bestellbar", "Bald verfügbar", "Auf Anfrage"].map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={() => saveModel(m.slug)} data-testid={`admin-save-${m.slug}`}
                          className="rounded-md bg-[#0A2540] hover:bg-slate-800 text-white">
                    Speichern
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
